import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react'
import { setSentryUser, clearSentryUser } from '@/sentry'

export type UserRole = 'employer' | 'employee' | 'super_admin'

export interface User {
  id: string
  email: string
  name: string
  pseudonym: string
  role: UserRole
  employerId?: number
  orgId?: number
  org_name?: string
  first_name?: string
  last_name?: string
  /** E.164 phone number, e.g. "+2348012345678" (phone auth users). */
  phone_number?: string | null
  /** ISO 3166-1 alpha-2 country of the phone number, e.g. "NG". */
  phone_country_iso2?: string | null
}

interface AuthContextValue {
  user: User | null
  token: string | null
  login: (token: string, responseData?: Record<string, unknown>) => Promise<void>
  logout: () => void
  isAuthenticated: boolean
}

export const AUTH_STORAGE_KEY = 'peerpass_auth'

interface StoredAuth {
  token: string
  refresh_token?: string
  user: User
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Lightweight JWT decode (no verification) — used only to read the exp claim
 * from a stored token on mount, without needing the secret.
 */
export function decodeJwtPayloadUnsafe(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(base64))
  } catch {
    return null
  }
}

/**
 * Return the number of milliseconds until the JWT expires.
 * Returns Infinity when there is no exp claim (token never expires).
 */
export function getExpiryMs(payload: Record<string, unknown>): number {
  const exp = payload.exp as number | undefined
  if (!exp) return Infinity
  return Math.max(0, exp * 1000 - Date.now())
}

/**
 * Extract a User object from a verified JWT payload and optional API response data.
 *
 * Merges data from multiple sources with a clear priority order:
 *   1. JWT payload (authoritative — it's signed)
 *   2. Top-level API response fields
 *   3. Nested objects: response.user, response.data, response.data.user
 *
 * Field name aliases (tried in order):
 *   id     ← sub, id, user_id
 *   email  ← email
 *   name   ← name, user_name
 *   pseudonym ← pseudonym
 *   role   ← role, user_role
 */
function extractUserFromPayload(
  payload: Record<string, unknown>,
  responseData?: Record<string, unknown>,
): User {
  // Build the prioritized list of sources to search for each field.
  const sources: Record<string, unknown>[] = [payload]

  if (responseData) {
    sources.push(responseData)
    const userObj = responseData.user as Record<string, unknown> | undefined
    const dataObj = responseData.data as Record<string, unknown> | undefined
    const dataUserObj = dataObj?.user as Record<string, unknown> | undefined
    if (userObj) sources.push(userObj)
    if (dataObj) sources.push(dataObj)
    if (dataUserObj) sources.push(dataUserObj)
  }

  /** Return the first truthy value found for any of the given keys across all sources. */
  const pick = (...keys: string[]): string | undefined => {
    for (const src of sources) {
      for (const key of keys) {
        const val = src[key]
        if (val !== undefined && val !== null && val !== '') {
          return String(val)
        }
      }
    }
    return undefined
  }

  const id = pick('sub', 'user_id', 'id') || ''
  const email = pick('email') || ''
  const name = pick('name') || ''
  const pseudonym = pick('pseudonym') || ''
  const employerId = pick('org_id', 'employer_id', 'employerId')
  const phoneNumber = pick('phone_number')
  const phoneCountryIso2 = pick('phone_country_iso2')

  const rawRole = pick('role', 'user_role')
  const role: UserRole =
    rawRole === 'employer' || rawRole === 'employee'
      ? rawRole
      : rawRole === 'super_admin'
        ? 'super_admin'
        : rawRole === 'company_admin' || rawRole === 'hr_manager' || rawRole === 'payroll_admin'
          ? 'employer'
          : 'employee'

  return {
    id,
    email,
    name,
    pseudonym,
    role,
    employerId: employerId ? Number(employerId) : undefined,
      orgId: payload.org_id ? Number(payload.org_id) : responseData?.org_id ? Number(responseData.org_id) : undefined,
      org_name: (payload.org_name as string) || (responseData?.org_name as string) || undefined,
      first_name: (payload.first_name as string) || (responseData?.first_name as string) || '',
      last_name: (payload.last_name as string) || (responseData?.last_name as string) || '',
      phone_number: phoneNumber || null,
      phone_country_iso2: phoneCountryIso2 || null,
  }
}

export function loadStoredAuth(): StoredAuth | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as StoredAuth
  } catch {
    localStorage.removeItem(AUTH_STORAGE_KEY)
    return null
  }
}

function saveStoredAuth(auth: StoredAuth): void {
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth))
}

function clearStoredAuth(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [initialized, setInitialized] = useState(false)

  // Restore session from localStorage on mount, clearing it if expired.
  useEffect(() => {
    const stored = loadStoredAuth()
    if (stored) {
      const payload = decodeJwtPayloadUnsafe(stored.token)
      if (payload && getExpiryMs(payload) > 0) {
        setToken(stored.token)
        setUser(stored.user)
        // Restore Sentry user context for error reporting
        setSentryUser({ id: stored.user.id, role: stored.user.role, orgId: stored.user.orgId })
      } else {
        clearStoredAuth()
      }
    }
    setInitialized(true)
  }, [])

  // Ref to hold the auto-logout timeout ID so we can clear it on manual logout.
  const autoLogoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Whenever the token changes (login, restore, or cleared), schedule or clear the auto-logout.
  useEffect(() => {
    // Clear any existing timeout
    if (autoLogoutRef.current !== null) {
      clearTimeout(autoLogoutRef.current)
      autoLogoutRef.current = null
    }

    if (!token) return

    const payload = decodeJwtPayloadUnsafe(token)
    if (!payload) return

    const remaining = getExpiryMs(payload)
    if (remaining <= 0) {
      // Token already expired — trigger logout (state update is batched)
      logout()
      return
    }

    // No exp claim — token doesn't expire, no auto-logout needed.
    if (!isFinite(remaining)) return

    autoLogoutRef.current = setTimeout(() => {
      logout()
    }, remaining)

    // Cleanup on unmount or when token changes.
    return () => {
      if (autoLogoutRef.current !== null) {
        clearTimeout(autoLogoutRef.current)
        autoLogoutRef.current = null
      }
    }
  }, [token])

  const login = useCallback(async (jwt: string, responseData?: Record<string, unknown>) => {
    // The API is the only trusted JWT verifier. The portal decodes claims for
    // display and relies on authenticated API calls for authorization.
    const payload = decodeJwtPayloadUnsafe(jwt)
    if (!payload) {
      throw new Error('Invalid JWT token')
    }

    const user = extractUserFromPayload(payload, responseData)
    const refreshToken = responseData?.refresh_token as string | undefined
    const stored: StoredAuth = {
      token: jwt,
      ...(refreshToken ? { refresh_token: refreshToken } : {}),
      user,
    }

    setToken(jwt)
    setUser(user)
    saveStoredAuth(stored)
    // Attach user context to Sentry for error reporting
    setSentryUser({ id: user.id, role: user.role, orgId: user.orgId })
  }, [])

  const logout = useCallback(async () => {
    // Clear any pending auto-logout timeout to avoid a double-logout race.
    if (autoLogoutRef.current !== null) {
      clearTimeout(autoLogoutRef.current)
      autoLogoutRef.current = null
    }

    // Call the server to invalidate the token, best-effort (don't block on failure)
    try {
      const { logoutApi } = await import('@/lib/api')
      await logoutApi()
    } catch {
      // Token invalidation failed — clear local state regardless
    }

    setToken(null)
    setUser(null)
    clearStoredAuth()
    clearSentryUser()
  }, [])

  // Don't render children until we've checked localStorage
  if (!initialized) return null

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAuthenticated: user !== null }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
